import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { FaChevronDown, FaChevronRight, FaCheck, FaTrash, FaEdit } from 'react-icons/fa';
import { addDoc, collection, serverTimestamp } from 'firebase/firestore';
import { db } from '../firebase';
import './Profile.css';
import JackTravelLinkageFields from './JackTravelLinkageFields';
import BikeOnboardingForm from './BikeOnboardingForm';
import { useAuth } from '../contexts/AuthContext';
import { useCommunityLinkagePresets } from '../hooks/useCommunityLinkagePresets';
import { useJackLinkageHandlers } from '../hooks/useJackLinkageHandlers';
import { isAdminUser } from '../utils/admin';
import {
  loadMainJackSettings,
  saveMainJackSettings,
  loadBikeProfilesStore,
  saveBikeProfilesStore,
  newJackBikeProfileId,
  loadUserLinkagePresets,
  saveUserLinkagePresets,
  loadAdvancedJackOverrides,
  saveAdvancedJackOverrides,
  JACK_ADVANCED_UI_FIELDS,
  JACK_ADVANCED_STRING_FIELDS,
  isJackOnboardingComplete,
  setJackOnboardingComplete,
  getInternalDefaultsForEditing,
  normalizeJackBikeLabel,
  bikeProfileDataSnapshot,
  bikeProfileDataSnapshotFromProfile,
  bikeProfileSnapshotsEqual,
  persistActiveJackBikeProfile,
} from '../utils/jackSettings';
/** Dropdown value: create a new saved bike profile (not persisted until Save). */
const NEW_BIKE_PROFILE_VALUE = '__new__';

function toOddNumber(value, min, max, fallback) {
  let n = Number(value);
  if (!Number.isFinite(n)) n = fallback;
  n = Math.max(min, Math.min(max, Math.round(n)));
  if (n % 2 === 0) n += 1;
  if (n > max) n = max % 2 === 0 ? max - 1 : max;
  return n;
}

const sectionHeaderButtonStyle = {
  width: '100%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  background: 'transparent',
  border: 'none',
  cursor: 'pointer',
  padding: 0,
  textAlign: 'left',
};

const sectionTitleStyle = {
  margin: 0,
  color: '#ffffff',
  fontSize: 18,
  fontWeight: 700,
  letterSpacing: '0.02em',
};

function CollapsibleSection({ title, expanded, onToggle, children }) {
  return (
    <div className="jack-section-inner">
      <button type="button" className="jack-collapsible-toggle" style={sectionHeaderButtonStyle} onClick={onToggle} aria-expanded={expanded}>
        <h3 className="jack-collapsible-title" style={sectionTitleStyle}>
          {title}
        </h3>
        {expanded ? <FaChevronDown color="#ffffff" aria-hidden /> : <FaChevronRight color="#ffffff" aria-hidden />}
      </button>
      {expanded ? <div style={{ marginTop: 14 }}>{children}</div> : null}
    </div>
  );
}

function pickAdvancedMerged() {
  const internal = getInternalDefaultsForEditing();
  const overrides = loadAdvancedJackOverrides();
  const out = {};
  JACK_ADVANCED_UI_FIELDS.forEach((k) => {
    if (JACK_ADVANCED_STRING_FIELDS.has(k)) {
      out[k] = overrides[k] !== undefined ? String(overrides[k]) : '';
    } else {
      out[k] = overrides[k] !== undefined ? overrides[k] : internal[k];
    }
  });
  return out;
}

const Settins = () => {
  const { user } = useAuth();
  const isAdmin = useMemo(() => isAdminUser(user, null), [user]);

  const [main, setMain] = useState(() => loadMainJackSettings());
  const [profiles, setProfiles] = useState(() => loadBikeProfilesStore().profiles);
  const [advanced, setAdvanced] = useState(() => pickAdvancedMerged());
  const [advancedOpen, setAdvancedOpen] = useState(false);
  const [bikeProfileNameInput, setBikeProfileNameInput] = useState('');
  const [userLinkagePresets, setUserLinkagePresets] = useState(() => loadUserLinkagePresets());
  /** Last persisted bike data for the active profile (for unsaved-changes detection). */
  const [bikeProfileBaseline, setBikeProfileBaseline] = useState(null);
  const [bikeProfileBaselineName, setBikeProfileBaselineName] = useState(null);
  const [bikeProfileEditMode, setBikeProfileEditMode] = useState(false);
  const [bikeProfileRenameInput, setBikeProfileRenameInput] = useState('');
  const [showOnboarding, setShowOnboarding] = useState(() => !isJackOnboardingComplete());
  const [publishLinkageAsPublic, setPublishLinkageAsPublic] = useState(false);

  useEffect(() => {
    if (!user?.uid) return;
    const store = loadBikeProfilesStore();
    let dirty = false;
    const nextProfiles = store.profiles.map((p) => {
      let q = { ...p };
      const nn = normalizeJackBikeLabel(p.name);
      if (nn !== String(p.name || '')) {
        q.name = nn;
        dirty = true;
      }
      if (p.linkageSource == null || p.linkageSource === '') {
        q = { ...q, linkageSource: 'custom' };
        dirty = true;
      }
      return q;
    });
    if (dirty) saveBikeProfilesStore({ profiles: nextProfiles });
    setProfiles(dirty ? nextProfiles : store.profiles);

    const list = loadUserLinkagePresets();
    let dirtyL = false;
    const nextLinkage = list.map((p) => {
      const nn = normalizeJackBikeLabel(p.name);
      if (nn !== String(p.name || '')) {
        dirtyL = true;
        return { ...p, name: nn };
      }
      return p;
    });
    if (dirtyL) saveUserLinkagePresets(nextLinkage);
    setUserLinkagePresets(dirtyL ? nextLinkage : list);

    setMain(loadMainJackSettings());
    setAdvanced(pickAdvancedMerged());
    setShowOnboarding(!isJackOnboardingComplete());
    setBikeProfileNameInput('');
    setPublishLinkageAsPublic(false);
  }, [user?.uid]);

  useEffect(() => {
    setBikeProfileEditMode(false);
    const pid = main.selectedBikeProfileId;
    if (!pid) {
      setBikeProfileBaseline(null);
      setBikeProfileBaselineName(null);
      setBikeProfileRenameInput('');
      return;
    }
    const p = profiles.find((x) => x.id === pid);
    if (p) {
      setBikeProfileBaseline(bikeProfileDataSnapshotFromProfile(p));
      setBikeProfileBaselineName(p.name);
      setBikeProfileRenameInput(p.name);
    }
  }, [main.selectedBikeProfileId, profiles]);

  const commitMain = useCallback((updater) => {
    setMain((prev) => {
      const next = typeof updater === 'function' ? updater(prev) : { ...prev, ...updater };
      saveMainJackSettings(next);
      return next;
    });
  }, []);

  const { communityPresets, communityListError } = useCommunityLinkagePresets();

  const afterSaveUserLinkagePreset = useCallback(
    async ({ nameNorm, rearA, rearB, rearC, rearD }) => {
      if (!isAdmin || !publishLinkageAsPublic || !user?.uid) return '';
      await addDoc(collection(db, 'publicLinkagePresets'), {
        publicLabel: nameNorm,
        rearA: Number(rearA),
        rearB: Number(rearB),
        rearC: Number(rearC),
        rearD: Number(rearD),
        rearTravelMm: main.rearTravel,
        frontTravelMm: main.frontTravel,
        creatorUid: user.uid,
        createdAt: serverTimestamp(),
        source: 'admin',
      });
      return 'Also published to the public library.';
    },
    [isAdmin, publishLinkageAsPublic, user?.uid, main.rearTravel, main.frontTravel],
  );

  const linkage = useJackLinkageHandlers({
    main,
    commitMain,
    communityPresets,
    userLinkagePresets,
    setUserLinkagePresets,
    afterSaveUserLinkagePreset,
  });

  const {
    linkageStatus,
    setLinkageStatus,
    linkagePresetNameInput,
    setLinkagePresetNameInput,
    updateMain,
    onLinkageSelect,
    handleLinkageCsv,
    handleSaveNewLinkagePreset,
    handleUpdateSavedLinkagePreset,
    deleteCurrentLinkagePreset,
    linkageSelectValue,
    linkageSavedSelected,
  } = linkage;

  useEffect(() => {
    const pid = main.selectedBikeProfileId;
    if (!pid || profiles.some((p) => p.id === pid)) return;
    commitMain((prev) => ({
      ...prev,
      selectedBikeProfileId: null,
      frontTravel: 0,
      rearTravel: 0,
    }));
  }, [main.selectedBikeProfileId, profiles, commitMain]);

  const applySavedProfile = useCallback(
    (profile) => {
      if (!profile) return;
      const linkageSource =
        typeof profile.linkageSource === 'string' && profile.linkageSource.length > 0
          ? profile.linkageSource
          : 'custom';
      commitMain((prev) => ({
        ...prev,
        selectedBikeProfileId: profile.id,
        frontTravel: profile.frontTravel,
        rearTravel: profile.rearTravel,
        rearA: profile.rearA,
        rearB: profile.rearB,
        rearC: profile.rearC,
        rearD: profile.rearD,
        linkageSource,
      }));
      setLinkageStatus('');
    },
    [commitMain, setLinkageStatus],
  );

  const onBikeProfileSelect = useCallback(
    (e) => {
      const v = e.target.value;
      if (v === NEW_BIKE_PROFILE_VALUE) {
        commitMain((prev) => ({ ...prev, selectedBikeProfileId: null }));
        setBikeProfileNameInput('');
        return;
      }
      const p = profiles.find((x) => x.id === v);
      if (p) applySavedProfile(p);
    },
    [profiles, applySavedProfile, commitMain],
  );

  /** Create a new saved bike profile (names stored lowercase). */
  const handleSaveBikeProfile = useCallback(() => {
    const nameNorm = normalizeJackBikeLabel(bikeProfileNameInput);
    if (!nameNorm) {
      alert('Enter a name for this bike profile.');
      return;
    }
    if (main.selectedBikeProfileId) return;
    if (profiles.some((p) => normalizeJackBikeLabel(p.name) === nameNorm)) {
      alert('A bike profile with this name already exists (names are not case-sensitive).');
      return;
    }
    const id = newJackBikeProfileId();
    const snap = bikeProfileDataSnapshot(main);
    const newP = {
      id,
      name: nameNorm,
      ...snap,
    };
    const updated = [...loadBikeProfilesStore().profiles, newP];
    saveBikeProfilesStore({ profiles: updated });
    setProfiles(updated);
    commitMain((prev) => ({ ...prev, selectedBikeProfileId: id }));
    setBikeProfileBaseline(bikeProfileDataSnapshot({ ...main, selectedBikeProfileId: id }));
    setBikeProfileBaselineName(nameNorm);
    setBikeProfileNameInput('');
    setLinkageStatus(`Saved bike profile “${nameNorm}”.`);
  }, [main, bikeProfileNameInput, commitMain, profiles]);

  const cancelBikeProfileEdit = useCallback(() => {
    const pid = main.selectedBikeProfileId;
    if (typeof pid !== 'string' || !pid) {
      setBikeProfileEditMode(false);
      return;
    }
    const p = loadBikeProfilesStore().profiles.find((x) => x.id === pid);
    if (p) applySavedProfile(p);
    const p2 = loadBikeProfilesStore().profiles.find((x) => x.id === pid);
    setBikeProfileRenameInput(p2 ? p2.name : '');
    setBikeProfileEditMode(false);
    setLinkageStatus('');
  }, [main.selectedBikeProfileId, applySavedProfile]);

  const handleOverwriteBikeProfile = useCallback(() => {
    const pid = main.selectedBikeProfileId;
    if (typeof pid !== 'string' || !pid) return;
    const nameNorm = normalizeJackBikeLabel(bikeProfileRenameInput);
    if (!nameNorm) {
      alert('Enter a name for this bike profile.');
      return;
    }
    if (profiles.some((p) => p.id !== pid && normalizeJackBikeLabel(p.name) === nameNorm)) {
      alert('A bike profile with this name already exists (names are not case-sensitive).');
      return;
    }
    persistActiveJackBikeProfile(main, { name: bikeProfileRenameInput });
    setProfiles(loadBikeProfilesStore().profiles);
    setBikeProfileBaseline(bikeProfileDataSnapshot(main));
    setBikeProfileBaselineName(nameNorm);
    setBikeProfileRenameInput(nameNorm);
    setBikeProfileEditMode(false);
    setLinkageStatus('Bike profile saved.');
  }, [main, profiles, bikeProfileRenameInput]);

  const deleteCurrentBikeProfile = useCallback(() => {
    const pid = main.selectedBikeProfileId;
    if (typeof pid !== 'string' || !pid) return;
    if (!window.confirm('Delete this bike profile? This cannot be undone.')) return;
    const store = loadBikeProfilesStore();
    const updated = store.profiles.filter((p) => p.id !== pid);
    saveBikeProfilesStore({ profiles: updated });
    setProfiles(updated);
    if (updated.length > 0) {
      applySavedProfile(updated[0]);
    } else {
      commitMain((prev) => ({
        ...prev,
        selectedBikeProfileId: null,
        frontTravel: 0,
        rearTravel: 0,
      }));
      setBikeProfileNameInput('');
    }
  }, [main.selectedBikeProfileId, applySavedProfile, commitMain]);

  const updateAdvancedField = useCallback((field, value) => {
    setAdvanced((prev) => {
      const next = { ...prev, [field]: value };
      const toSave = {};
      JACK_ADVANCED_UI_FIELDS.forEach((k) => {
        toSave[k] = next[k];
      });
      saveAdvancedJackOverrides(toSave);
      return next;
    });
  }, []);

  const filterStatus = useMemo(() => {
    const type =
      advanced.filterType === 'savgol'
        ? 'Savitzky-Golay'
        : advanced.filterType === 'moving_average'
          ? 'Moving Average'
          : 'None';
    return `Filter: ${type} | Position window: ${advanced.positionFilterWindow} | Velocity window: ${advanced.velocityFilterWindow}`;
  }, [advanced.filterType, advanced.positionFilterWindow, advanced.velocityFilterWindow]);

  const sortedBikeProfiles = useMemo(
    () =>
      [...profiles].sort((a, b) =>
        String(a.name || '').localeCompare(String(b.name || ''), undefined, { sensitivity: 'base' }),
      ),
    [profiles],
  );

  const sortedLinkagePresets = useMemo(
    () =>
      [...userLinkagePresets].sort((a, b) =>
        String(a.name || '').localeCompare(String(b.name || ''), undefined, { sensitivity: 'base' }),
      ),
    [userLinkagePresets],
  );

  const bikeSelectValue = useMemo(() => {
    const pid = main.selectedBikeProfileId;
    if (pid && profiles.some((p) => p.id === pid)) return pid;
    return NEW_BIKE_PROFILE_VALUE;
  }, [main.selectedBikeProfileId, profiles]);

  const showNewBikeProfileRow = !main.selectedBikeProfileId;

  const linkageSourceSummary = useMemo(() => {
    const src = main.linkageSource;
    if (!src || src === 'custom') return 'Custom (coefficients in editor)';
    if (typeof src === 'string' && src.startsWith('saved:')) {
      const id = src.slice('saved:'.length);
      const pr = userLinkagePresets.find((p) => p.id === id);
      return pr ? `Saved preset: ${pr.name}` : `Saved preset (${id.slice(0, 8)}…)`;
    }
    if (typeof src === 'string' && src.startsWith('community:')) {
      const id = src.slice('community:'.length);
      const pr = communityPresets.find((p) => p.id === id);
      const label = pr?.publicLabel || pr?.name;
      return label ? `Public library: ${label}` : 'Public library preset';
    }
    return String(src);
  }, [main.linkageSource, userLinkagePresets, communityPresets]);

  const travelDirty = useMemo(() => {
    if (!main.selectedBikeProfileId || bikeProfileBaseline == null) return false;
    return !bikeProfileSnapshotsEqual(bikeProfileDataSnapshot(main), bikeProfileBaseline);
  }, [main, bikeProfileBaseline]);

  const nameDirty = useMemo(() => {
    if (!main.selectedBikeProfileId || !bikeProfileEditMode) return false;
    return (
      normalizeJackBikeLabel(bikeProfileRenameInput || '') !==
      normalizeJackBikeLabel(bikeProfileBaselineName || '')
    );
  }, [main.selectedBikeProfileId, bikeProfileEditMode, bikeProfileRenameInput, bikeProfileBaselineName]);

  const showBikeSaveButton =
    Boolean(main.selectedBikeProfileId) && bikeProfileEditMode && (travelDirty || nameDirty);

  const showBikeSavedIndicator =
    Boolean(main.selectedBikeProfileId) &&
    !bikeProfileEditMode &&
    bikeProfileBaseline != null &&
    !travelDirty;

  const linkagePresetFooterAdminSlot = useMemo(
    () =>
      isAdmin ? (
        <label className="menu-item" style={{ marginTop: 14, display: 'flex', alignItems: 'flex-start', gap: 8 }}>
          <input
            type="checkbox"
            checked={publishLinkageAsPublic}
            onChange={(e) => setPublishLinkageAsPublic(e.target.checked)}
          />
          <span style={{ color: '#aaa', fontSize: 13, lineHeight: 1.35 }}>
            Also publish this curve to the public library (visible to all users in Linkage preset → Public library).
            Your private saved preset is still stored only for your account.
          </span>
        </label>
      ) : null,
    [isAdmin, publishLinkageAsPublic],
  );

  return (
    <div className="profile-container">
      {showOnboarding ? (
        <div
          className="modal-overlay"
          style={{ position: 'fixed', inset: 0, zIndex: 2000, background: 'rgba(0,0,0,0.75)' }}
          role="dialog"
          aria-modal="true"
          aria-labelledby="bike-onboard-title"
        >
          <div
            className="modal-content jack-onboarding-modal-shell"
            style={{ maxWidth: 'min(560px, calc(100vw - 32px))', margin: '6vh auto', padding: 20 }}
            onClick={(e) => e.stopPropagation()}
          >
            <BikeOnboardingForm
              variant="modal"
              showSkip
              onCompleted={() => {
                const nextMain = loadMainJackSettings();
                const nextProfiles = loadBikeProfilesStore().profiles;
                setMain(nextMain);
                setProfiles(nextProfiles);
                const p = nextProfiles.find((x) => x.id === nextMain.selectedBikeProfileId);
                setBikeProfileBaseline(p ? bikeProfileDataSnapshotFromProfile(p) : null);
                setBikeProfileBaselineName(p ? p.name : null);
                setBikeProfileRenameInput(p ? p.name : '');
                setShowOnboarding(false);
              }}
              onSkip={() => {
                setJackOnboardingComplete();
                setShowOnboarding(false);
              }}
            />
          </div>
        </div>
      ) : null}

      <div className="profile-section card jack-settings-root" data-cursor-element-id="cursor-el-1">
        <h2 className="section-title">Bike &amp; suspension</h2>
        <p style={{ color: '#888', marginTop: 0, marginBottom: 16, fontSize: 14 }}>
          Bike profiles and your saved linkage curves are stored <strong style={{ color: '#aaa' }}>per Firebase account</strong>{' '}
          on this browser. Choose a profile or <strong style={{ color: '#aaa' }}>New bike profile</strong> to name and save
          your setup. Names are lowercase. For a saved profile, use <strong style={{ color: '#aaa' }}>Edit</strong>, then{' '}
          <strong style={{ color: '#aaa' }}>Save changes</strong> at the bottom of that section. Public library curves are
          shared for everyone.
        </p>

        <div className="jack-section-inner">
          <h3 style={{ ...sectionTitleStyle, marginBottom: 12 }}>Bike profile</h3>
          <p style={{ color: '#666', fontSize: 12, margin: '0 0 10px' }}>
            Saved profiles store travel, coefficients, and the selected linkage preset (saved, community, or custom).
            For an existing profile, use <strong style={{ color: '#999' }}>Edit</strong> to change name, travel, or
            linkage — then <strong style={{ color: '#999' }}>Save changes</strong> at the bottom of this section. Use{' '}
            <strong style={{ color: '#999' }}>New bike profile</strong> to save the current setup under a new name
            (names are stored lowercase).
          </p>
          <div className="jack-settings-stack" style={{ marginBottom: 16 }}>
            <label className="profile-email jack-settings-label" htmlFor="bikeProfileSelect">
              Active bike profile
            </label>
            <div className="jack-settings-toolbar">
              <select
                id="bikeProfileSelect"
                className="form-input"
                value={bikeSelectValue}
                onChange={onBikeProfileSelect}
                disabled={bikeProfileEditMode}
                title={bikeProfileEditMode ? 'Save or cancel edits before switching profile' : undefined}
              >
                {sortedBikeProfiles.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.name}
                  </option>
                ))}
                <option value={NEW_BIKE_PROFILE_VALUE}>New bike profile</option>
              </select>
              {main.selectedBikeProfileId ? (
                <div className="jack-settings-toolbar-actions">
                  {bikeProfileEditMode ? (
                    <button
                      type="button"
                      className="btn btn-secondary"
                      onClick={cancelBikeProfileEdit}
                      title="Discard edits and reload this profile from storage"
                    >
                      Cancel
                    </button>
                  ) : (
                    <button
                      type="button"
                      className="jack-icon-edit"
                      onClick={() => {
                        const p = profiles.find((x) => x.id === main.selectedBikeProfileId);
                        setBikeProfileRenameInput(p ? p.name : '');
                        setBikeProfileEditMode(true);
                      }}
                      aria-label="Edit bike profile"
                      title="Edit name, travel, and linkage"
                    >
                      <FaEdit aria-hidden />
                    </button>
                  )}
                  <button
                    type="button"
                    className="jack-icon-trash"
                    onClick={deleteCurrentBikeProfile}
                    aria-label="Delete bike profile"
                    title="Delete bike profile"
                  >
                    <FaTrash aria-hidden />
                  </button>
                </div>
              ) : null}
            </div>
            {showNewBikeProfileRow ? (
              <div className="jack-settings-subsection">
                <label className="profile-email jack-settings-label" htmlFor="bikeProfileName">
                  New bike profile name
                </label>
                <div className="jack-settings-inline-row">
                  <input
                    id="bikeProfileName"
                    className="form-input"
                    placeholder="e.g. Trail bike, DH rig"
                    value={bikeProfileNameInput}
                    onChange={(e) => setBikeProfileNameInput(e.target.value)}
                  />
                  <button type="button" className="btn btn-bike-profile-save" onClick={handleSaveBikeProfile}>
                    Save bike profile
                  </button>
                </div>
              </div>
            ) : null}
          </div>

          {main.selectedBikeProfileId && !bikeProfileEditMode ? (
            <div className="jack-bike-profile-readonly">
              <div className="jack-stats-grid jack-stats-grid--2">
                <div>
                  <div className="jack-settings-label">Profile name</div>
                  <div className="jack-bike-profile-readonly-value">
                    {profiles.find((x) => x.id === main.selectedBikeProfileId)?.name || '—'}
                  </div>
                </div>
                <div>
                  <div className="jack-settings-label">Front travel (mm)</div>
                  <div className="jack-bike-profile-readonly-value">{main.frontTravel}</div>
                </div>
                <div>
                  <div className="jack-settings-label">Rear travel (mm)</div>
                  <div className="jack-bike-profile-readonly-value">{main.rearTravel}</div>
                </div>
                <div style={{ gridColumn: '1 / -1' }}>
                  <div className="jack-settings-label">Linkage source</div>
                  <div className="jack-bike-profile-readonly-value">{linkageSourceSummary}</div>
                </div>
              </div>
              {showBikeSavedIndicator ? (
                <div className="jack-bike-profile-saved-row" title="All changes are saved to this profile">
                  <FaCheck aria-hidden />
                  <span>Saved</span>
                </div>
              ) : null}
            </div>
          ) : null}

          {main.selectedBikeProfileId && bikeProfileEditMode ? (
            <div className="jack-bike-profile-edit-block">
              <div className="jack-settings-subsection" style={{ marginBottom: 16 }}>
                <label className="profile-email jack-settings-label" htmlFor="bikeProfileRename">
                  Profile name
                </label>
                <input
                  id="bikeProfileRename"
                  className="form-input"
                  placeholder="e.g. trail bike, dh rig"
                  value={bikeProfileRenameInput}
                  onChange={(e) => setBikeProfileRenameInput(e.target.value)}
                  autoComplete="off"
                />
              </div>
              <JackTravelLinkageFields
                idPrefix="settings"
                main={main}
                updateMain={updateMain}
                linkageSelectValue={linkageSelectValue}
                onLinkageSelect={onLinkageSelect}
                sortedLinkagePresets={sortedLinkagePresets}
                communityPresets={communityPresets}
                communityListError={communityListError}
                handleLinkageCsv={handleLinkageCsv}
                linkagePresetNameInput={linkagePresetNameInput}
                setLinkagePresetNameInput={setLinkagePresetNameInput}
                handleSaveNewLinkagePreset={handleSaveNewLinkagePreset}
                handleUpdateSavedLinkagePreset={handleUpdateSavedLinkagePreset}
                linkageSavedSelected={linkageSavedSelected}
                deleteCurrentLinkagePreset={deleteCurrentLinkagePreset}
                linkageStatus={linkageStatus}
                linkagePresetFooterSlot={linkagePresetFooterAdminSlot}
              />
              <div className="jack-bike-profile-footer">
                <button
                  type="button"
                  className="btn btn-bike-profile-save"
                  onClick={handleOverwriteBikeProfile}
                  disabled={!showBikeSaveButton}
                >
                  Save changes
                </button>
                {!showBikeSaveButton ? (
                  <span className="jack-bike-profile-footer-hint">Change something above to enable save.</span>
                ) : null}
              </div>
            </div>
          ) : null}

          {!main.selectedBikeProfileId ? (
            <JackTravelLinkageFields
              idPrefix="settings"
              main={main}
              updateMain={updateMain}
              linkageSelectValue={linkageSelectValue}
              onLinkageSelect={onLinkageSelect}
              sortedLinkagePresets={sortedLinkagePresets}
              communityPresets={communityPresets}
              communityListError={communityListError}
              handleLinkageCsv={handleLinkageCsv}
              linkagePresetNameInput={linkagePresetNameInput}
              setLinkagePresetNameInput={setLinkagePresetNameInput}
              handleSaveNewLinkagePreset={handleSaveNewLinkagePreset}
              handleUpdateSavedLinkagePreset={handleUpdateSavedLinkagePreset}
              linkageSavedSelected={linkageSavedSelected}
              deleteCurrentLinkagePreset={deleteCurrentLinkagePreset}
              linkageStatus={linkageStatus}
              linkagePresetFooterSlot={linkagePresetFooterAdminSlot}
            />
          ) : null}
        </div>

        <CollapsibleSection title="Advanced settings" expanded={advancedOpen} onToggle={() => setAdvancedOpen((o) => !o)}>
          <p style={{ color: '#666', fontSize: 13, marginTop: 0 }}>
            Safe defaults are loaded from <code style={{ color: '#999' }}>src/config/jackInternalDefaults.json</code>.
            Changes here are stored in your browser and override that file for tuning or debugging.
          </p>

          <div className="profile-menu jack-advanced-menu">
            <h4 style={{ color: '#aaa', fontSize: 14, margin: '16px 0 8px' }}>ADC polarity</h4>
            <p style={{ color: '#888', fontSize: 13, marginTop: 0, marginBottom: 8 }}>
              If wheel travel reads backwards, invert that end so compression maps correctly (same as Jack benchmark).
            </p>
            <label className="menu-item" htmlFor="invertFront">
              <input
                id="invertFront"
                type="checkbox"
                checked={Boolean(advanced.invertFront)}
                onChange={(e) => updateAdvancedField('invertFront', e.target.checked)}
              />
              <span style={{ marginLeft: 8 }}>Invert fork</span>
            </label>
            <label className="menu-item" htmlFor="invertRear">
              <input
                id="invertRear"
                type="checkbox"
                checked={Boolean(advanced.invertRear)}
                onChange={(e) => updateAdvancedField('invertRear', e.target.checked)}
              />
              <span style={{ marginLeft: 8 }}>Invert rear shock</span>
            </label>

            <h4 style={{ color: '#aaa', fontSize: 14, margin: '16px 0 8px' }}>Signal filtering</h4>
            <div className="jack-stats-grid jack-stats-grid--3">
              <div>
                <label className="profile-email" htmlFor="filterType">
                  Filter type
                </label>
                <select
                  id="filterType"
                  className="form-input"
                  value={advanced.filterType}
                  onChange={(e) => updateAdvancedField('filterType', e.target.value)}
                >
                  <option value="savgol">Savitzky-Golay</option>
                  <option value="moving_average">Moving average</option>
                  <option value="none">None (raw)</option>
                </select>
              </div>
              <div>
                <label className="profile-email" htmlFor="medianFilterSize">
                  Median filter size
                </label>
                <input
                  id="medianFilterSize"
                  className="form-input"
                  type="number"
                  min="1"
                  max="99"
                  step="2"
                  value={advanced.medianFilterSize}
                  onChange={(e) => updateAdvancedField('medianFilterSize', toOddNumber(e.target.value, 1, 99, 5))}
                />
              </div>
              <div>
                <label className="profile-email" htmlFor="maxPosChange">
                  Max position change (mm)
                </label>
                <input
                  id="maxPosChange"
                  className="form-input"
                  type="number"
                  min="1"
                  step="1"
                  value={advanced.maxPosChange}
                  onChange={(e) => updateAdvancedField('maxPosChange', Number(e.target.value))}
                />
              </div>
              <div>
                <label className="profile-email" htmlFor="velocityWindow">
                  Velocity window
                </label>
                <input
                  id="velocityWindow"
                  className="form-input"
                  type="number"
                  min="3"
                  max="99"
                  step="2"
                  value={advanced.velocityWindow}
                  onChange={(e) => updateAdvancedField('velocityWindow', toOddNumber(e.target.value, 3, 99, 21))}
                />
              </div>
              <div>
                <label className="profile-email" htmlFor="positionFilterWindow">
                  Position smoothing
                </label>
                <input
                  id="positionFilterWindow"
                  className="form-input"
                  type="number"
                  min="1"
                  max="20"
                  step="2"
                  value={advanced.positionFilterWindow}
                  onChange={(e) =>
                    updateAdvancedField('positionFilterWindow', toOddNumber(e.target.value, 1, 20, 5))
                  }
                />
              </div>
              <div>
                <label className="profile-email" htmlFor="velocityFilterWindow">
                  Velocity smoothing
                </label>
                <input
                  id="velocityFilterWindow"
                  className="form-input"
                  type="number"
                  min="1"
                  max="50"
                  step="2"
                  value={advanced.velocityFilterWindow}
                  onChange={(e) =>
                    updateAdvancedField('velocityFilterWindow', toOddNumber(e.target.value, 1, 50, 11))
                  }
                />
              </div>
            </div>
            <label className="menu-item" htmlFor="showRawData">
              <input
                id="showRawData"
                type="checkbox"
                checked={Boolean(advanced.showRawData)}
                onChange={(e) => updateAdvancedField('showRawData', e.target.checked)}
              />
              <span style={{ marginLeft: 8 }}>Show raw data overlay</span>
            </label>
            <label className="menu-item" htmlFor="autoReprocess">
              <input
                id="autoReprocess"
                type="checkbox"
                checked={Boolean(advanced.autoReprocess)}
                onChange={(e) => updateAdvancedField('autoReprocess', e.target.checked)}
              />
              <span style={{ marginLeft: 8 }}>Auto-reprocess on filter change</span>
            </label>
            <div style={{ color: '#888', fontSize: 13 }}>{filterStatus}</div>

            <h4 style={{ color: '#aaa', fontSize: 14, margin: '16px 0 8px' }}>Stroke analysis</h4>
            <div className="jack-stats-grid jack-stats-grid--3">
              <div>
                <label className="profile-email" htmlFor="minStrokeDepth">
                  Min stroke depth (mm)
                </label>
                <input
                  id="minStrokeDepth"
                  className="form-input"
                  type="number"
                  min="0"
                  value={advanced.minStrokeDepth}
                  onChange={(e) => updateAdvancedField('minStrokeDepth', Number(e.target.value))}
                />
              </div>
              <div>
                <label className="profile-email" htmlFor="minStrokeSamples">
                  Min stroke samples
                </label>
                <input
                  id="minStrokeSamples"
                  className="form-input"
                  type="number"
                  min="1"
                  value={advanced.minStrokeSamples}
                  onChange={(e) => updateAdvancedField('minStrokeSamples', Number(e.target.value))}
                />
              </div>
              <div>
                <label className="profile-email" htmlFor="bottomOutThreshold">
                  Bottom-out threshold (%)
                </label>
                <input
                  id="bottomOutThreshold"
                  className="form-input"
                  type="number"
                  min="0"
                  max="100"
                  value={advanced.bottomOutThreshold}
                  onChange={(e) => updateAdvancedField('bottomOutThreshold', Number(e.target.value))}
                />
              </div>
              <div>
                <label className="profile-email" htmlFor="topOutThreshold">
                  Top-out threshold (mm)
                </label>
                <input
                  id="topOutThreshold"
                  className="form-input"
                  type="number"
                  min="0"
                  value={advanced.topOutThreshold}
                  onChange={(e) => updateAdvancedField('topOutThreshold', Number(e.target.value))}
                />
              </div>
            </div>

            <h4 style={{ color: '#aaa', fontSize: 14, margin: '16px 0 8px' }}>Packing detection</h4>
            <div className="jack-stats-grid jack-stats-grid--3">
              <div>
                <label className="profile-email" htmlFor="extensionThreshold">
                  Extension threshold (mm)
                </label>
                <input
                  id="extensionThreshold"
                  className="form-input"
                  type="number"
                  min="0"
                  value={advanced.extensionThreshold}
                  onChange={(e) => updateAdvancedField('extensionThreshold', Number(e.target.value))}
                />
              </div>
              <div>
                <label className="profile-email" htmlFor="packedThreshold">
                  Packed threshold (mm)
                </label>
                <input
                  id="packedThreshold"
                  className="form-input"
                  type="number"
                  min="0"
                  value={advanced.packedThreshold}
                  onChange={(e) => updateAdvancedField('packedThreshold', Number(e.target.value))}
                />
              </div>
              <div>
                <label className="profile-email" htmlFor="minPackedHits">
                  Min packed hits
                </label>
                <input
                  id="minPackedHits"
                  className="form-input"
                  type="number"
                  min="2"
                  value={advanced.minPackedHits}
                  onChange={(e) => updateAdvancedField('minPackedHits', Number(e.target.value))}
                />
              </div>
              <div>
                <label className="profile-email" htmlFor="stuckThreshold">
                  Stuck threshold (mm)
                </label>
                <input
                  id="stuckThreshold"
                  className="form-input"
                  type="number"
                  min="0"
                  value={advanced.stuckThreshold}
                  onChange={(e) => updateAdvancedField('stuckThreshold', Number(e.target.value))}
                />
              </div>
              <div>
                <label className="profile-email" htmlFor="ratchetDelta">
                  Ratchet delta (mm)
                </label>
                <input
                  id="ratchetDelta"
                  className="form-input"
                  type="number"
                  min="0"
                  value={advanced.ratchetDelta}
                  onChange={(e) => updateAdvancedField('ratchetDelta', Number(e.target.value))}
                />
              </div>
              <div>
                <label className="profile-email" htmlFor="velocityThreshold">
                  Velocity threshold (mm/s)
                </label>
                <input
                  id="velocityThreshold"
                  className="form-input"
                  type="number"
                  min="0"
                  value={advanced.velocityThreshold}
                  onChange={(e) => updateAdvancedField('velocityThreshold', Number(e.target.value))}
                />
              </div>
            </div>

            <h4 style={{ color: '#aaa', fontSize: 14, margin: '16px 0 8px' }}>Summary tuning guide (icons + AI)</h4>
            <p style={{ color: '#888', fontSize: 12, margin: '0 0 10px', lineHeight: 1.4 }}>
              Bands below drive thumbs-up vs warning icons on the Ride <strong>Suspension → Summary</strong> tab and Statistics
              tables. They do not change the Jack engine—only how results are flagged.
            </p>
            <div className="jack-stats-grid jack-stats-grid--3">
              <div>
                <label className="profile-email" htmlFor="tuneReboundP95Min">
                  Rebound p95 min (mm/s)
                </label>
                <input
                  id="tuneReboundP95Min"
                  className="form-input"
                  type="number"
                  min="0"
                  step="10"
                  value={advanced.tuneReboundP95Min}
                  onChange={(e) => updateAdvancedField('tuneReboundP95Min', Number(e.target.value))}
                />
              </div>
              <div>
                <label className="profile-email" htmlFor="tuneReboundP95Max">
                  Rebound p95 max (mm/s)
                </label>
                <input
                  id="tuneReboundP95Max"
                  className="form-input"
                  type="number"
                  min="0"
                  step="10"
                  value={advanced.tuneReboundP95Max}
                  onChange={(e) => updateAdvancedField('tuneReboundP95Max', Number(e.target.value))}
                />
              </div>
              <div>
                <label className="profile-email" htmlFor="tuneCompressionP95Min">
                  Compression p95 min (mm/s)
                </label>
                <input
                  id="tuneCompressionP95Min"
                  className="form-input"
                  type="number"
                  min="0"
                  step="50"
                  value={advanced.tuneCompressionP95Min}
                  onChange={(e) => updateAdvancedField('tuneCompressionP95Min', Number(e.target.value))}
                />
              </div>
              <div>
                <label className="profile-email" htmlFor="tuneCompressionP95Max">
                  Compression p95 max (mm/s)
                </label>
                <input
                  id="tuneCompressionP95Max"
                  className="form-input"
                  type="number"
                  min="0"
                  step="50"
                  value={advanced.tuneCompressionP95Max}
                  onChange={(e) => updateAdvancedField('tuneCompressionP95Max', Number(e.target.value))}
                />
              </div>
              <div>
                <label className="profile-email" htmlFor="tuneDynamicSagPctMin">
                  Dynamic sag % min
                </label>
                <input
                  id="tuneDynamicSagPctMin"
                  className="form-input"
                  type="number"
                  min="0"
                  max="100"
                  step="1"
                  value={advanced.tuneDynamicSagPctMin}
                  onChange={(e) => updateAdvancedField('tuneDynamicSagPctMin', Number(e.target.value))}
                />
              </div>
              <div>
                <label className="profile-email" htmlFor="tuneDynamicSagPctMax">
                  Dynamic sag % max
                </label>
                <input
                  id="tuneDynamicSagPctMax"
                  className="form-input"
                  type="number"
                  min="0"
                  max="100"
                  step="1"
                  value={advanced.tuneDynamicSagPctMax}
                  onChange={(e) => updateAdvancedField('tuneDynamicSagPctMax', Number(e.target.value))}
                />
              </div>
              <div>
                <label className="profile-email" htmlFor="tuneBalanceRatioMin">
                  Balance ratio F/R min
                </label>
                <input
                  id="tuneBalanceRatioMin"
                  className="form-input"
                  type="number"
                  min="0.1"
                  max="5"
                  step="0.01"
                  value={advanced.tuneBalanceRatioMin}
                  onChange={(e) => updateAdvancedField('tuneBalanceRatioMin', Number(e.target.value))}
                />
              </div>
              <div>
                <label className="profile-email" htmlFor="tuneBalanceRatioMax">
                  Balance ratio F/R max
                </label>
                <input
                  id="tuneBalanceRatioMax"
                  className="form-input"
                  type="number"
                  min="0.1"
                  max="5"
                  step="0.01"
                  value={advanced.tuneBalanceRatioMax}
                  onChange={(e) => updateAdvancedField('tuneBalanceRatioMax', Number(e.target.value))}
                />
              </div>
              <div>
                <label className="profile-email" htmlFor="tuneMaxBottomOutsTotal">
                  Max bottom-outs total (F+R)
                </label>
                <input
                  id="tuneMaxBottomOutsTotal"
                  className="form-input"
                  type="number"
                  min="0"
                  step="1"
                  value={advanced.tuneMaxBottomOutsTotal}
                  onChange={(e) => updateAdvancedField('tuneMaxBottomOutsTotal', Number(e.target.value))}
                />
              </div>
              <div>
                <label className="profile-email" htmlFor="tuneMaxBottomOutsPerEnd">
                  Max bottom-outs per end
                </label>
                <input
                  id="tuneMaxBottomOutsPerEnd"
                  className="form-input"
                  type="number"
                  min="0"
                  step="1"
                  value={advanced.tuneMaxBottomOutsPerEnd}
                  onChange={(e) => updateAdvancedField('tuneMaxBottomOutsPerEnd', Number(e.target.value))}
                />
              </div>
              <div>
                <label className="profile-email" htmlFor="tunePackingIndexGoodMax">
                  Packing index “good” max (≤ = OK)
                </label>
                <input
                  id="tunePackingIndexGoodMax"
                  className="form-input"
                  type="number"
                  min="0"
                  max="100"
                  step="1"
                  value={advanced.tunePackingIndexGoodMax}
                  onChange={(e) => updateAdvancedField('tunePackingIndexGoodMax', Number(e.target.value))}
                />
              </div>
              <div>
                <label className="profile-email" htmlFor="tuneMinTravelUsePct">
                  Min travel use % (warn if lower with 0 BO)
                </label>
                <input
                  id="tuneMinTravelUsePct"
                  className="form-input"
                  type="number"
                  min="0"
                  max="100"
                  step="1"
                  value={advanced.tuneMinTravelUsePct}
                  onChange={(e) => updateAdvancedField('tuneMinTravelUsePct', Number(e.target.value))}
                />
              </div>
              <div>
                <label className="profile-email" htmlFor="tuneRoughnessWarnAbove">
                  Roughness warn above (0 = off)
                </label>
                <input
                  id="tuneRoughnessWarnAbove"
                  className="form-input"
                  type="number"
                  min="0"
                  step="0.1"
                  value={advanced.tuneRoughnessWarnAbove}
                  onChange={(e) => updateAdvancedField('tuneRoughnessWarnAbove', Number(e.target.value))}
                />
              </div>
              <div style={{ gridColumn: '1 / -1' }}>
                <label className="profile-email" htmlFor="tuneOpenaiApiKey">
                  OpenAI API key (optional — stored in this browser only)
                </label>
                <input
                  id="tuneOpenaiApiKey"
                  className="form-input"
                  type="password"
                  autoComplete="off"
                  placeholder="sk-… or use REACT_APP_OPENAI_API_KEY at build"
                  value={advanced.tuneOpenaiApiKey}
                  onChange={(e) => updateAdvancedField('tuneOpenaiApiKey', e.target.value)}
                />
              </div>
            </div>
          </div>
        </CollapsibleSection>
      </div>
    </div>
  );
};

export default Settins;
